import { makeStyles } from "tss-react/mui";
import AuthLayout from "./AuthLayout";
import { useState } from "react";
import { Alert, Typography, Paper, IconButton } from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "src/redux/hooks";
import { isEmpty, isValidEmail, maxCharacters } from "src/helpers/validation-utils";
import { cloneObjectBasic } from "src/helpers/utils";
import { IUserStore } from "src/redux/user/types";
import { LoginUser } from "src/redux/user/userReducer";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import SecurityIcon from '@mui/icons-material/Security';
import InventoryIcon from '@mui/icons-material/Inventory';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BarChartIcon from '@mui/icons-material/BarChart';
import GenericButton from "src/components/GenericButton";
import { DataService } from "src/services/data-service";

interface ILoginApiResponse {
  success: boolean;
  token: string;
  message: string
  data: {
    id: number;
    name: string;
    email: string;
    role: string;
  }
}

const useStyles = makeStyles()((theme) => {
  return {
    fullScreenContainer: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: "100vw",
      height: "100vh",
      background: "linear-gradient(135deg, #0078d4 0%, #106ebe 25%, #005a9e 50%, #004578 75%, #003152 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "16px",
      boxSizing: "border-box",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute" as const,
        top: "-100px",
        left: "-100px",
        right: "-100px",
        bottom: "-100px",
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px),
          linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
        `,
        backgroundSize: "60px 60px, 60px 60px, 20px 20px, 20px 20px",
        animation: "gridMove 15s linear infinite",
        zIndex: 0,
      }
    },
    contentWrapper: {
      width: "100%",
      maxWidth: "1200px",
      display: "flex",
      gap: "40px",
      alignItems: "stretch",
      position: "relative" as const,
      zIndex: 1,
      minHeight: "500px",
    },
    leftPanel: {
      flex: "0 0 45%",
      display: "flex",
      flexDirection: "column" as const,
      justifyContent: "center",
      color: "white",
      padding: "32px 20px",
    },
    rightPanel: {
      flex: "0 0 55%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    brandContainer: {
      marginBottom: "32px",
    },
    logoSection: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      marginBottom: "24px",
    },
    logoIcon: {
      width: "56px",
      height: "56px",
      borderRadius: "16px",
      background: "rgba(255, 255, 255, 0.15)",
      backdropFilter: "blur(10px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      border: "2px solid rgba(255, 255, 255, 0.2)",
    },
    brandTitle: {
      fontSize: "28px",
      fontWeight: 800,
      color: "white",
      margin: 0,
      lineHeight: 1.2,
      textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
    },
    brandSubtitle: {
      fontSize: "16px",
      color: "rgba(255, 255, 255, 0.9)",
      fontWeight: 500,
      margin: 0,
    },
    featuresList: {
      listStyle: "none",
      padding: 0,
      margin: "24px 0 0 0",
    },
    featureItem: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "16px",
      fontSize: "15px",
      color: "rgba(255, 255, 255, 0.9)",
      fontWeight: 500,
    },
    featureIcon: {
      width: "36px",
      height: "36px",
      borderRadius: "10px",
      background: "rgba(255, 255, 255, 0.1)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      border: "1px solid rgba(255, 255, 255, 0.1)",
    },
    loginCard: {
      backgroundColor: "white",
      borderRadius: "20px",
      padding: "32px",
      boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15), 0 8px 20px rgba(0, 0, 0, 0.1)",
      border: "1px solid rgba(255, 255, 255, 0.3)",
      position: "relative" as const,
      overflow: "hidden",
      width: "100%",
      maxWidth: "480px",
      minWidth: "320px",
      maxHeight: "90vh",
      overflowY: "auto",
      "&::before": {
        content: '""',
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        height: "4px",
        background: "linear-gradient(90deg, #0078d4 0%, #106ebe 50%, #0078d4 100%)",
      }
    },
    loginHeader: {
      textAlign: "center" as const,
      marginBottom: "28px",
    },
    loginTitle: {
      fontSize: "24px",
      fontWeight: 700,
      color: "#1f2937",
      margin: "0 0 6px 0",
    },
    loginSubtitle: {
      fontSize: "14px",
      color: "#6b7280",
      fontWeight: 500,
      margin: 0,
    },
    formContainer: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "20px",
      width: "100%",
    },
    inputWrapper: {
      position: "relative" as const,
      width: "100%",
    },
    customInput: {
      width: "100%",
      "& .MuiInputLabel-root": {
        fontSize: "14px",
        fontWeight: 600,
        color: "#374151",
        transform: "translate(14px, 14px) scale(1)",
        "&.MuiInputLabel-shrink": {
          transform: "translate(14px, -9px) scale(0.75)",
        },
        "&.Mui-focused": {
          color: "#0078d4",
        },
        "&.Mui-error": {
          color: "#ef4444",
        }
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: "12px",
        backgroundColor: "#fafbfc",
        fontSize: "15px",
        fontWeight: 500,
        transition: "all 0.2s ease",
        minHeight: "48px",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#e1e5e9",
          borderWidth: "1.5px",
        },
        "&:hover": {
          backgroundColor: "#f5f7fa",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#0078d4",
          }
        },
        "&.Mui-focused": {
          backgroundColor: "white",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#0078d4",
            borderWidth: "2px",
          },
          boxShadow: "0 0 0 3px rgba(0, 120, 212, 0.08)",
        },
        "&.Mui-error": {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#ef4444",
            borderWidth: "1.5px",
          },
          backgroundColor: "#fef7f7",
          "&:hover": {
            backgroundColor: "#fef2f2",
          },
        }
      },
      "& .MuiOutlinedInput-input": {
        padding: "12px 14px",
        fontSize: "15px",
        height: "auto",
        "&::placeholder": {
          color: "#9ca3af",
          opacity: 1,
        }
      },
      "& .MuiInputAdornment-root": {
        marginRight: "8px",
        "& .MuiSvgIcon-root": {
          fontSize: "20px",
          color: "#6b7280",
        }
      },
      "& .MuiFormHelperText-root": {
        fontSize: "12px",
        marginTop: "6px",
        marginLeft: "4px",
        fontWeight: 500,
        "&.Mui-error": {
          color: "#ef4444",
        }
      }
    },
    passwordToggle: {
      color: "#6b7280",
      padding: "4px",
      "&:hover": {
        color: "#0078d4",
        backgroundColor: "rgba(0, 120, 212, 0.08)",
      }
    },
    submitButton: {
      marginTop: "16px",
      width: "100%",
    },
    customButton: {
      width: "100%",
      borderRadius: "12px",
      padding: "14px 24px",
      fontSize: "15px",
      fontWeight: 600,
      textTransform: "none" as const,
      background: "linear-gradient(135deg, #0078d4 0%, #106ebe 100%)",
      boxShadow: "0 6px 20px rgba(0, 120, 212, 0.25)",
      transition: "all 0.2s ease",
      border: "none",
      color: "white",
      minHeight: "48px",
      "&:hover": {
        background: "linear-gradient(135deg, #106ebe 0%, #005a9e 100%)",
        boxShadow: "0 8px 25px rgba(0, 120, 212, 0.3)",
        transform: "translateY(-1px)",
      },
      "&:active": {
        transform: "translateY(0px)",
      },
      "&:disabled": {
        background: "#e5e7eb",
        color: "#9ca3af",
        boxShadow: "none",
        transform: "none",
      }
    },
    alertContainer: {
      marginBottom: "20px",
      "& .MuiAlert-root": {
        borderRadius: "10px",
        padding: "12px 16px",
        fontSize: "13px",
        fontWeight: 500,
        border: "1px solid #fecaca",
        backgroundColor: "#fef2f2",
        color: "#991b1b",
        "& .MuiAlert-icon": {
          fontSize: "18px",
        }
      }
    },
    securitySection: {
      marginTop: "24px",
      padding: "16px",
      backgroundColor: "#f0f8ff",
      borderRadius: "10px",
      border: "1px solid #b3d9ff",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    securityIcon: {
      color: "#0078d4",
      fontSize: "20px",
      flexShrink: 0,
    },
    securityText: {
      fontSize: "12px",
      color: "#1e40af",
      lineHeight: 1.4,
      fontWeight: 500,
    },
    loadingOverlay: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "20px",
      zIndex: 10,
      backdropFilter: "blur(2px)",
    },
    loadingText: {
      marginTop: "12px",
      color: "#0078d4",
      fontSize: "14px",
      fontWeight: 600,
    },
    spinner: {
      width: "32px",
      height: "32px",
      border: "3px solid #e5e7eb",
      borderTop: "3px solid #0078d4",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    "@keyframes spin": {
      "0%": { transform: "rotate(0deg)" },
      "100%": { transform: "rotate(360deg)" }
    },
    "@keyframes gridMove": {
      "0%": { 
        transform: "translate(0, 0)"
      },
      "100%": { 
        transform: "translate(60px, 60px)"
      }
    },
    // Responsive breakpoints
    "@media (max-width: 1024px)": {
      contentWrapper: {
        gap: "24px",
        maxWidth: "900px",
      },
      leftPanel: {
        flex: "0 0 40%",
        padding: "24px 16px",
      },
      rightPanel: {
        flex: "0 0 60%",
      },
      brandTitle: {
        fontSize: "24px",
      },
      brandSubtitle: {
        fontSize: "14px",
      },
      loginCard: {
        padding: "28px",
        maxWidth: "420px",
      }
    },
    "@media (max-width: 768px)": {
      fullScreenContainer: {
        padding: "12px",
      },
      contentWrapper: {
        flexDirection: "column" as const,
        gap: "16px",
        maxWidth: "400px",
        minHeight: "auto",
      },
      leftPanel: {
        display: "none",
      },
      rightPanel: {
        flex: "1",
      },
      loginCard: {
        padding: "24px",
        maxWidth: "100%",
        minWidth: "280px",
      },
      loginTitle: {
        fontSize: "20px",
      },
      loginSubtitle: {
        fontSize: "13px",
      }
    },
    "@media (max-height: 700px)": {
      loginCard: {
        padding: "24px",
        maxHeight: "85vh",
      },
      loginHeader: {
        marginBottom: "20px",
      },
      formContainer: {
        gap: "16px",
      },
      securitySection: {
        marginTop: "16px",
        padding: "12px",
      },
      securityText: {
        fontSize: "11px",
      }
    },
    "@media (max-height: 600px)": {
      fullScreenContainer: {
        padding: "8px",
      },
      loginCard: {
        padding: "20px",
        maxHeight: "90vh",
      },
      loginHeader: {
        marginBottom: "16px",
      },
      loginTitle: {
        fontSize: "20px",
      },
      loginSubtitle: {
        fontSize: "12px",
      },
      formContainer: {
        gap: "14px",
      },
      securitySection: {
        display: "none", // Hide on very small screens
      }
    }
  };
});

interface ILoginPageFormState {
  values: {
    email: string;
    password: string;
  };
  errors: {
    email: string;
    password: string;
  };
  touched: {
    email: boolean;
    password: boolean;
  };
}

const getInitialLoginFormState = (): ILoginPageFormState => {
  return {
    values: {
      email: "",
      password: "",
    },
    errors: {
      email: "",
      password: "",
    },
    touched: {
      email: false,
      password: false,
    },
  };
};

const LoginPage: React.FC = () => {
  const { classes } = useStyles();

  const [pageState, setPageState] = useState<ILoginPageFormState>(
    getInitialLoginFormState()
  );

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showError, setShowError] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onValueChange = (name: string, value: string) => {
    const newPageState = cloneObjectBasic(pageState);
    if (name === "email") {
      newPageState.values.email = value;
      newPageState.errors.email = "";
    } else if (name === "password") {
      newPageState.values.password = value;
      newPageState.errors.password = "";
    }
    setErrorMessage("");
    setShowError(false);
    setPageState(newPageState);
  };

  const onBlur = (name: string) => {
    const newPageState = cloneObjectBasic(pageState);
    if (name === "email") {
      newPageState.touched.email = true;
      newPageState.errors.email = validateField(name, pageState.values.email);
    } else if (name === "password") {
      newPageState.touched.password = true;
      newPageState.errors.password = validateField(
        name,
        pageState.values.password
      );
    }
    setPageState(newPageState);
  };

  const validateField = (name: string, value: string): string => {
    if (name === "email") {
      if (isEmpty(value)) {
        return "Username or email is required";
      }
    } else if (name === "password") {
      if (isEmpty(value)) {
        return "Password is required";
      }
      if (!maxCharacters(value, 50)) {
        return "Password cannot exceed 50 characters";
      }
    }
    return "";
  };

  const validateForm = () => {
    const newPageState = cloneObjectBasic(pageState);

    newPageState.errors = {
      email: validateField("email", newPageState.values.email),
      password: validateField("password", newPageState.values.password),
    };

    newPageState.touched = {
      email: true,
      password: true,
    };

    setPageState(newPageState);

    return !(newPageState.errors.email || newPageState.errors.password);
  };

  const submit = async () => {
    if (!submitting) {
      setSubmitting(true);
      const isValid = validateForm();
      if (isValid) {
        const data = {
          email: pageState.values.email,
          password: pageState.values.password,
        };

        try {
          const loginRequest = await DataService.post(
            "api/User/login", data);

          let result: ILoginApiResponse | null = null;
          try {
            result = await loginRequest.json();
          } catch (e) {
            // ignore JSON parse error
          }

          if (loginRequest.ok && result && result.success) {
            const newUser: IUserStore = {
              isAuthenticated: true,
              user: {
                email: result.data.email,
                id: result.data.id.toString(),
                name: result.data.name,
                role: result.data.role || '',
              },
              token: result.token,
            };

            dispatch(LoginUser(newUser));
            setSubmitting(false);
            setErrorMessage("");
            setShowError(false);
            toast.success("Welcome back! Login successful.");
            navigate("/");
          } else if (result && result.success === false && result.message) {
            setErrorMessage(result.message);
            setShowError(true);
            toast.error(result.message);
            setSubmitting(false);
          } else if (loginRequest.status === 401) {
            setErrorMessage("Invalid credentials. Please check your username/email and password and try again.");
            setShowError(true);
            toast.error("Invalid credentials");
            setSubmitting(false);
          } else {
            setErrorMessage("Unable to connect to the server. Please try again in a moment.");
            setShowError(true);
            toast.error("Connection error");
            setSubmitting(false);
          }
        } catch (error) {
          setErrorMessage("Network error. Please check your internet connection and try again.");
          setShowError(true);
          toast.error("Network error");
          setSubmitting(false);
        }
      } else {
        setSubmitting(false);
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div className={classes.fullScreenContainer}>
      <div className={classes.contentWrapper}>
        {/* Left Panel - Branding */}
        <div className={classes.leftPanel}>
          <div className={classes.brandContainer}>
            <div className={classes.logoSection}>
              <div className={classes.logoIcon}>
                <WarehouseIcon sx={{ fontSize: "28px" }} />
              </div>
              <div>
                <Typography className={classes.brandTitle}>
                  Embassy App
                </Typography>
                <Typography className={classes.brandSubtitle}>
                  Lebanese Embassy in Kinshasa
                </Typography>
              </div>
            </div>

            <ul className={classes.featuresList}>
              <li className={classes.featureItem}>
                <div className={classes.featureIcon}>
                  <InventoryIcon sx={{ fontSize: "18px" }} />
                </div>
                <span>Real-time inventory tracking and management</span>
              </li>
              <li className={classes.featureItem}>
                <div className={classes.featureIcon}>
                  <LocalShippingIcon sx={{ fontSize: "18px" }} />
                </div>
                <span>Streamlined order fulfillment and shipping</span>
              </li>
              <li className={classes.featureItem}>
                <div className={classes.featureIcon}>
                  <BarChartIcon sx={{ fontSize: "18px" }} />
                </div>
                <span>Advanced analytics and reporting dashboard</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className={classes.rightPanel}>
          <Paper className={classes.loginCard} elevation={0}>
            {submitting && (
              <div className={classes.loadingOverlay}>
                <div className={classes.spinner}></div>
                <Typography className={classes.loadingText}>
                  Authenticating your credentials...
                </Typography>
              </div>
            )}
            
            <div className={classes.loginHeader}>
              <Typography className={classes.loginTitle}>
                Welcome to Embassy App
              </Typography>
              <Typography className={classes.loginSubtitle}>
                Please sign in
              </Typography>
            </div>

            {showError && errorMessage && (
              <div className={classes.alertContainer}>
                <Alert
                  severity="error"
                  onClose={() => setShowError(false)}
                  sx={{ 
                    borderRadius: "10px",
                    "& .MuiAlert-action": {
                      padding: "2px 4px"
                    }
                  }}
                >
                  {errorMessage}
                </Alert>
              </div>
            )}

            <form 
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
              onKeyDown={handleKeyPress}
              className={classes.formContainer}
            >
              <div className={classes.inputWrapper}>
                <TextField
                  label="Username or Email"
                  type="text"
                  value={pageState.values.email}
                  onChange={(e) => onValueChange("email", e.target.value)}
                  name="email"
                  error={!!(pageState.touched.email && pageState.errors.email)}
                  helperText={pageState.touched.email ? pageState.errors.email : ""}
                  onBlur={() => onBlur("email")}
                  disabled={submitting}
                  placeholder="Enter your username or email"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  className={classes.customInput}
                />
              </div>

              <div className={classes.inputWrapper}>
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={pageState.values.password}
                  onChange={(e) => onValueChange("password", e.target.value)}
                  name="password"
                  error={!!(pageState.touched.password && pageState.errors.password)}
                  helperText={pageState.touched.password ? pageState.errors.password : ""}
                  onBlur={() => onBlur("password")}
                  disabled={submitting}
                  placeholder="Enter your password"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={submitting}
                          className={classes.passwordToggle}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  className={classes.customInput}
                />
              </div>

              <div className={classes.submitButton}>
                <GenericButton
                  type="submit"
                  color="primary"
                  variant="contained"
                  onClick={submit}
                  disabled={submitting}
                  text={submitting ? "Signing In..." : "Access Warehouse"}
                  className={classes.customButton}
                />
              </div>
            </form>

            <div className={classes.securitySection}>
              <SecurityIcon className={classes.securityIcon} />
              <Typography className={classes.securityText}>
                Your warehouse data is protected with enterprise-grade security. 
                All operations are logged and monitored for compliance and audit purposes.
              </Typography>
            </div>
          </Paper>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;